/**
 * Order editing window Card — BLU-718.
 *
 * How long this org's NEW orders wait before a fulfilment partner can batch
 * them, so a customer can still edit or cancel in Shopify. The org that OWNS
 * the order sets this, because that org is the one handling customer edits —
 * it then applies at whichever warehouse fulfils, including a third-party one.
 *
 * Stored on `organizations.order_hold_minutes` and applied at order intake by
 * the `apply_order_hold_setting()` trigger. `null` = inherit the 2-hour
 * default; `0` = no hold. The value takes precedence over the fulfilling
 * location's own order-hold switch.
 *
 * Self-contained: drives its own tRPC query + mutation; not wired to the
 * Organization tab's profile save form. Reads gated to org members, the Save
 * button + mutation to admins — mirrors CustomArtworkSourceCard.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Clock, Loader2, RefreshCw, Save } from 'lucide-react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useRole } from '@/hooks/use-role';
import { trpc } from '@/lib/trpc';

const TOAST_ID = {
  saveSuccess: 'order-hold-save-success',
  saveError: 'order-hold-save-error',
} as const;

/** Sentinel for "no explicit value" — Radix Select cannot hold an empty value. */
const INHERIT = 'inherit';

/**
 * Presets rather than a free-text number: no validation surface, no unit
 * ambiguity, and the bounds can't drift from the DB CHECK (0..1440).
 *
 * `sentence` is a separate field, not `label.toLowerCase()` — the INHERIT
 * label is a phrase ("Use the warehouse default"), so reusing it inside a
 * sentence produced "…will be held for use the warehouse default before…".
 */
const HOLD_OPTIONS: Array<{ value: string; label: string; sentence: string }> = [
  {
    value: INHERIT,
    label: 'Use the warehouse default',
    // Deliberately not "2 hours": NULL falls through to the fulfilling
    // location's own hold switch, which that warehouse may have turned OFF.
    sentence: 'the warehouse default, usually 2 hours, unless the warehouse fulfilling your orders has holds turned off',
  },
  { value: '0', label: 'No hold, batch immediately', sentence: 'no time' },
  { value: '15', label: '15 minutes', sentence: '15 minutes' },
  { value: '30', label: '30 minutes', sentence: '30 minutes' },
  { value: '45', label: '45 minutes', sentence: '45 minutes' },
  { value: '60', label: '1 hour', sentence: '1 hour' },
  { value: '90', label: '1 hour 30 minutes', sentence: '1 hour 30 minutes' },
  { value: '120', label: '2 hours', sentence: '2 hours' },
  { value: '180', label: '3 hours', sentence: '3 hours' },
  { value: '240', label: '4 hours', sentence: '4 hours' },
  { value: '480', label: '8 hours', sentence: '8 hours' },
  { value: '720', label: '12 hours', sentence: '12 hours' },
  { value: '1440', label: '24 hours', sentence: '24 hours' },
];

export function OrderHoldWindowCard() {
  const { can, isAdmin } = useRole();

  // Mirror the sibling cards: hide entirely without org-settings read (also
  // covers a 3PL viewing another org's settings).
  if (!can('org_settings.read')) {
    return null;
  }

  return <OrderHoldWindowCardInner admin={isAdmin()} />;
}

function OrderHoldWindowCardInner({ admin }: { admin: boolean }) {
  const utils = trpc.useUtils();

  const orgQuery = trpc.organization.getOrganization.useQuery(undefined, {
    staleTime: 60_000,
  });

  const [value, setValue] = useState<string>(INHERIT);
  const [hasChanges, setHasChanges] = useState(false);

  // Seed from the loaded org — but never over in-progress edits. staleTime is
  // 60s, so a refocus/remount refetch would otherwise clobber unsaved changes.
  // The dirty flag is read through a ref, not a dependency, so the save's
  // onSuccess flipping it false can't re-run this against the pre-save cache.
  // (Same reasoning as CustomArtworkSourceCard.)
  const hasChangesRef = useRef(hasChanges);
  hasChangesRef.current = hasChanges;
  useEffect(() => {
    if (!orgQuery.data || hasChangesRef.current) return;
    const minutes = orgQuery.data.organization?.order_hold_minutes;
    setValue(minutes === null || minutes === undefined ? INHERIT : String(minutes));
  }, [orgQuery.data]);

  const updateOrg = trpc.organization.updateOrganization.useMutation({
    onSuccess: () => {
      setHasChanges(false);
      void utils.organization.getOrganization.invalidate();
      toast.success('Order editing window saved', { id: TOAST_ID.saveSuccess });
    },
    onError: (err) => {
      toast.error(`Could not save: ${err.message}`, { id: TOAST_ID.saveError });
    },
  });

  const handleSave = () => {
    updateOrg.mutate({
      orderHoldMinutes: value === INHERIT ? null : Number(value),
    });
  };

  // Splice in a one-off option when the stored value isn't a preset (set by
  // support/SQL, or a preset removed later) — otherwise Radix renders an empty
  // trigger and the sentence below silently describes the wrong window.
  const options = useMemo(() => {
    if (value === INHERIT || HOLD_OPTIONS.some((o) => o.value === value)) return HOLD_OPTIONS;
    const mins = Number(value);
    const label = `${mins} minutes`;
    return [...HOLD_OPTIONS, { value, label, sentence: label }].sort(
      (a, b) => (a.value === INHERIT ? -1 : b.value === INHERIT ? 1 : Number(a.value) - Number(b.value))
    );
  }, [value]);

  const selected = options.find((o) => o.value === value);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-4 w-4" aria-hidden="true" />
          Order editing window
        </CardTitle>
        <CardDescription>
          How long new orders wait before they can go into production, so a customer can still
          edit or cancel in Shopify. This applies wherever your orders are fulfilled, including
          a third-party warehouse.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Three-branch loading / error / form, mirroring CustomArtworkSourceCard.
            Without the error branch a failed load left the Select sitting on the
            default option — indistinguishable from a real saved value, so an
            admin would read a window this org has not actually got. */}
        {orgQuery.isLoading ? (
          <div className="space-y-2 max-w-sm">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ) : orgQuery.isError ? (
          <div className="space-y-3">
            <p className="text-sm text-destructive">
              Couldn&apos;t load the order editing window. Please retry.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void orgQuery.refetch()}
              disabled={orgQuery.isFetching}
              className="gap-1.5"
            >
              <RefreshCw
                className={orgQuery.isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'}
                aria-hidden="true"
              />
              Retry
            </Button>
          </div>
        ) : (
          <>
            <div className="space-y-2 max-w-sm">
              <Label htmlFor="order-hold-window">Hold new orders for</Label>
              <Select
                value={value}
                onValueChange={(next) => {
                  setValue(next);
                  setHasChanges(true);
                }}
                disabled={!admin}
              >
                <SelectTrigger id="order-hold-window">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {options.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <p className="text-sm text-muted-foreground">
                {value === '0'
                  ? 'Orders can be produced as soon as they arrive. Any edit a customer makes after that may be too late.'
                  : `New orders will be held for ${selected?.sentence ?? 'the warehouse default'} before production can start.`}
              </p>
            </div>

            <p className="text-sm text-muted-foreground">
              Changing this affects new orders only. Orders already waiting keep the window they
              were given.
            </p>

            {admin ? (
              <div className="flex justify-end">
                <Button onClick={handleSave} disabled={!hasChanges || updateOrg.isPending}>
                  {updateOrg.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <Save className="h-4 w-4" aria-hidden="true" />
                  )}
                  Save
                </Button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Only administrators can change the order editing window.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
